import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Camera, ArrowRight } from "lucide-react";
import { useCms } from "../lib/cms";
import { track } from "../lib/pixel";

const WA_PRESET = "Hallo, ich möchte ein Angebot für einen Umzug / Transport / eine Entrümpelung erhalten.";

export default function StickyScanCTA() {
  const { pathname } = useLocation();
  const { cms } = useCms();
  if (pathname.startsWith("/admin")) return null;

  const rawPhone = (cms?.legal?.impressum?.phone) || "+49 176 56198464";
  const digits = rawPhone.replace(/\D/g, "") || "4917656198464";
  const waHref = `https://wa.me/${digits}?text=${encodeURIComponent(WA_PRESET)}`;

  const isScanPage = pathname.startsWith("/scan");
  const isClearancePage = pathname.startsWith("/entruempelung");
  const primaryHref = isClearancePage ? "#lead-form" : "/angebot?type=full";
  const primaryLabel = isClearancePage ? "Angebot anfordern" : "Angebot anfordern";

  return (
    <>
      {/* Mobile bottom bar — primary lead CTA (65%) + WhatsApp secondary outline (35%) */}
      <div
        className="fixed bottom-0 inset-x-0 z-50 md:hidden bg-white border-t-2 border-slate-200 shadow-[0_-12px_32px_rgba(15,23,42,0.14)]"
        data-testid="sticky-mobile-bar"
      >
        <div className="px-3 py-2.5 flex items-center gap-2">
          {isClearancePage ? (
            <a
              href="#lead-form"
              className="basis-[65%] inline-flex items-center justify-center gap-1.5 rounded-2xl bg-brand-amber text-brand-navy px-4 py-3.5 text-[14px] font-extrabold shadow-soft active:scale-[0.98] transition-all"
              data-testid="sticky-lead-cta"
            >
              {primaryLabel} <ArrowRight size={16} />
            </a>
          ) : (
            <Link
              to={primaryHref}
              onClick={() => track({ custom: "PrimaryCtaClicked", params: { source: "sticky_mobile" } })}
              className="basis-[65%] inline-flex items-center justify-center gap-1.5 rounded-2xl bg-brand-amber text-brand-navy px-4 py-3.5 text-[14px] font-extrabold shadow-soft active:scale-[0.98] transition-all"
              data-testid="sticky-lead-cta"
            >
              {primaryLabel} <ArrowRight size={16} />
            </Link>
          )}
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            className="basis-[35%] inline-flex items-center justify-center gap-1.5 rounded-2xl border-2 border-green-500 text-green-700 bg-white px-3 py-3.5 text-[14px] font-bold active:scale-[0.98] transition-all"
            data-testid="sticky-whatsapp-mobile"
          >
            <svg viewBox="0 0 32 32" fill="currentColor" aria-hidden="true" className="w-4 h-4">
              <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.155-.146-.342-.347-.567-.617-.275-.317-.498-.62-.668-.91-.106-.18-.054-.345.155-.488.205-.142.387-.314.578-.486.146-.13.265-.27.319-.448.083-.272.066-.487-.054-.65-.144-.196-.39-.547-.737-1.058-.349-.508-.564-.793-.667-.793h-.515c-.27 0-.602.198-.602.198-.227.1-.422.345-.604.554-.357.41-.557.96-.557 1.622 0 .638.218 1.27.654 1.892.434.624.99 1.207 1.666 1.748.7.564 1.469 1.057 2.31 1.484.84.43 1.673.71 2.498.836a.95.95 0 0 0 .312.057c.527 0 .917-.16 1.166-.483.249-.323.373-.69.373-1.106 0-.247-.06-.385-.183-.46-.122-.073-.348-.13-.677-.17z M16 4C9.373 4 4 9.373 4 16c0 2.111.55 4.094 1.515 5.823L4 28l6.354-1.665A11.948 11.948 0 0 0 16 28c6.627 0 12-5.373 12-12S22.627 4 16 4zm0 22a9.95 9.95 0 0 1-5.073-1.385l-.363-.215-3.768.988 1.005-3.671-.236-.378A9.952 9.952 0 0 1 6 16c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10z"/>
            </svg>
            WhatsApp
          </a>
        </div>
      </div>

      {/* Desktop floating scanner button — only outside /scan page */}
      {!isScanPage && (
        <Link
          to="/scan"
          onClick={() => track({ custom: "ScannerOpened" })}
          className="hidden md:inline-flex group fixed bottom-7 right-7 z-50 items-center gap-2.5 rounded-2xl bg-brand-amber text-brand-navy pl-5 pr-6 py-4 text-[15px] font-extrabold shadow-floating hover:bg-amber-300 hover:scale-[1.04] transition-all"
          data-testid="sticky-scan-desktop"
        >
          <span className="relative grid place-items-center">
            <span className="absolute inset-[-6px] rounded-full bg-brand-amber/30 animate-pulse-ring" />
            <Camera size={20} strokeWidth={2.4} className="relative" />
          </span>
          Preis prüfen
        </Link>
      )}

      {/* Mobile spacer so content isn't hidden behind sticky bar */}
      <div className="md:hidden h-20" aria-hidden="true" />
    </>
  );
}
